// @ts-nocheck
import {useEffect, useMemo, useRef, useState} from 'react'
import {useTable, ColumnInstance, Row} from 'react-table'
import {CustomHeaderColumn} from '../table/columns/CustomHeaderColumn'
import {usersColumns} from './columns/_columns'
import {User} from '../core/_models'
import {KTCardBody} from '../../../../../_metronic/helpers'
import {CustomRow} from './columns/CustomRow'
import {UsersListLoading} from '../components/loading/UsersListLoading'
import {UsersListPagination} from '../components/pagination/UsersListPagination'
import axios from 'axios'
import {BASE_URL} from '../../../../../api'
import {useParams, useNavigate} from 'react-router-dom'
import {useSelector} from 'react-redux'

type SearchProps = {
  userSearch: String
  setUserSearch: Function
  loading?: boolean
  setLoading?: Function
  onDataChange?: (data: any[]) => void
}

const UsersTable = ({
  sDate,
  eDate,
  genderFilter,
  setGenderFilter,
  userSearch,
  setUserSearch,
  page,
  setPage,
  typeFilter,
  setTypeFilter,
  locationFilter,
  setLocationFilter,
  applyFilter,
  setApplyFilter,
  loading: parentLoading,
  setLoading: setParentLoading,
  onDataChange,
}: SearchProps) => {
  const [loading, setLoading] = useState<boolean>(true)
  const [userData, setUserData] = useState<User[]>([])
  const [paginationDetails, setPaginationDetails] = useState<any>({})
  const {p} = useParams()
  const navigate = useNavigate()
  const isInitialMount = useRef(true)

  const userFilter = useSelector((state) => state.filter.usersFilter)

  // Keep `page` in sync with the URL's page segment (deep links, and the
  // browser back/forward button after visiting a client's details page).
  // This only reacts to the URL changing - it must NOT re-run every time
  // `page` itself changes, or it immediately stomps a page change that
  // just happened locally (a pagination click, a filter reset) back to
  // whatever the URL still said. That mismatch was what made pages
  // "revert" while filtering/paging, and made the back button from a
  // client's details page always land on page 1.
  useEffect(() => {
    if (p) {
      const parsedPage = parseInt(p, 10)
      if (!isNaN(parsedPage) && parsedPage > 0 && parsedPage !== page) {
        setPage(parsedPage)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p])

  // Whenever the search term or an active filter changes, go back to page
  // 1 and update the URL to match - but not on first mount, so a deep
  // link / back-navigation to a specific page isn't immediately reset.
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    if (page !== 1) {
      setPage(1)
      navigate('/apps/agents/1')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSearch, userFilter])

  useEffect(() => {
    // Going back to the list from a practitioner's details page remounts
    // this component fresh with page=1, then the URL-sync effect above
    // immediately corrects it to whatever page the URL says (e.g. 11) -
    // see that effect's comment. That's two renders in quick succession,
    // so this effect fires twice back to back: once for page 1, once for
    // page 11, both in flight at the same time with no request
    // cancellation. Whichever response happened to land last used to win,
    // so on a slow page-1 response the table could end up showing page 1's
    // rows while the pagination control below (driven by the same `page`
    // state, not by this fetch) correctly highlighted page 11 - exactly
    // the "back button shows page 1 but page 11 is selected" bug, and
    // intermittent because it depends on which request's response arrives
    // first. Guarding with AbortController so only the most recent
    // request for the current `page`/search/filter can ever update state.
    const controller = new AbortController()

    const fetchData = async () => {
      try {
        setLoading(true)
        const response = await axios.get(`${BASE_URL}/admin/users`, {
          params: {
            page,
            search: userSearch,
            startDate: userFilter.startDate,
            endDate: userFilter.endDate,
            location: userFilter.location,
            type: userFilter.type,
            appVisibility: userFilter.visibility,
          },
          signal: controller.signal,
        })

        console.log('Response:', response.data)
        const {docs, ...pagination} = response.data
        setUserData(docs)
        setPaginationDetails(pagination)
        setLoading(false)

        // Notify parent component about data change
        if (onDataChange) {
          onDataChange(docs)
        }

        // Update parent loading state if provided
        if (setParentLoading) {
          setParentLoading(false)
        }
      } catch (error) {
        if (axios.isCancel(error)) {
          // Superseded by a newer request (page/search/filter changed
          // again before this one resolved) - not a real error, and the
          // request that replaced it owns updating loading/state.
          return
        }

        console.error('Error fetching data:', error)
        setLoading(false)

        // Update parent loading state if provided
        if (setParentLoading) {
          setParentLoading(false)
        }
      }
    }

    fetchData()

    return () => {
      controller.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSearch, page, userFilter])

  const getSearchUsers = (filterkeyword) => {
    return userData
  }

  const searchUsers = getSearchUsers(userSearch)

  // Use searchUsers if available, otherwise use userData
  const displayData = searchUsers.length > 0 || userSearch ? searchUsers : userData

  const data = useMemo(() => displayData, [displayData])
  const columns = useMemo(() => usersColumns, [])
  console.log('Data for table: ', data)
  const {getTableProps, getTableBodyProps, headers, rows, prepareRow} = useTable({
    columns,
    data,
  })

  console.log('rows', rows)

  return (
    <KTCardBody className='py-4'>
      <div className='table-responsive'>
        <table
          id='kt_table_users'
          className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'
          {...getTableProps()}
        >
          <thead>
            <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
              {headers.map((column: ColumnInstance<User>) => (
                <CustomHeaderColumn key={column.id} column={column} />
              ))}
            </tr>
          </thead>
          <tbody className='text-gray-600 fw-bold' {...getTableBodyProps()}>
            {rows.length > 0 ? (
              rows.map((row: Row<User>, i) => {
                prepareRow(row)
                return <CustomRow row={row} key={`row-${i}-${row.id}`} />
              })
            ) : (
              <tr>
                <td colSpan={7}>
                  <div className='d-flex text-center w-100 align-content-center justify-content-center'>
                    No matching records found
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <UsersListPagination
        page={page}
        setPage={setPage}
        totalPages={paginationDetails?.totalPages}
        loading={loading}
        paginationDetails={paginationDetails}
      />
      {loading && <UsersListLoading />}
    </KTCardBody>
  )
}

export {UsersTable}
